import React from 'react';

const RAW_API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const API_BASE = RAW_API.replace(/\/api\/?$/, '');
const DEFAULT_LOGO = '/NIS ABJ LOGO.jpg';

// ─── Static branch details shown on every membership card ───
export const ORG_INFO = {
  name: 'The Nigerian Institution of Surveyors',
  branch: 'FCT Branch',
  address: 'Plot 718, Cadastral Zone C00, Institutions and Research District, Abuja FCT',
  website: 'www.nisfct.com.ng',
  email: 'nisfctbranch@gmail.com',
};

/**
 * Media URLs coming from the API may be absolute (build_absolute_uri),
 * root-relative ("/media/..") or bare ("media/.."). Normalise them all
 * to a browser-loadable absolute URL.
 */
export const resolveMedia = (u) => {
  if (!u) return null;
  if (/^https?:\/\//i.test(u) || u.startsWith('data:')) return u;
  return `${API_BASE}${u.startsWith('/') ? u : `/${u}`}`;
};

const cardName = (member) =>
  member?.full_name ||
  [member?.user?.first_name, member?.user?.last_name].filter(Boolean).join(' ') ||
  '—';

const cardDesignation = (member) =>
  member?.designation_display ||
  member?.designation ||
  member?.membership_category_display ||
  member?.membership_category ||
  'Member';

const cardEmploymentCategory = (member) => ({
  private_sector: 'Private Sector',
  public_sector: 'Public Sector',
}[member?.employment_category] || 'Not specified');

const initial = (member) => (cardName(member).charAt(0) || 'N').toUpperCase();

/**
 * On-screen digital membership ID card (Tailwind).
 * Shared by the member portal page and the admin preview.
 */
export default function MembershipIDCard({ member, logo }) {
  if (!member) return null;
  const photo = resolveMedia(member.photo);
  const qr = resolveMedia(member.qr_code);
  const logoUrl = resolveMedia(logo) || DEFAULT_LOGO;
  const employmentCategory = cardEmploymentCategory(member);

  return (
    <div className="w-[340px] max-w-full bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
      {/* Header band */}
      <div className="bg-gradient-to-br from-[#062E10] to-[#0B5D1E] px-5 pt-5 pb-8 text-center relative">
        <div className="flex items-center justify-center gap-2.5">
          <img
            src={logoUrl}
            alt="NIS"
            className="h-11 w-11 object-contain bg-white rounded-full p-1 shadow"
            onError={(e) => { e.currentTarget.src = DEFAULT_LOGO; }}
          />
          <div className="text-left">
            <p className="text-white font-bold text-[13px] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {ORG_INFO.name}
            </p>
            <p className="text-[#C8A951] text-[11px] font-semibold tracking-wide">{ORG_INFO.branch}</p>
          </div>
        </div>
        <p className="mt-3 inline-block text-[10px] uppercase tracking-[0.2em] text-white/80 border border-white/25 rounded-full px-3 py-0.5">
          Membership ID Card
        </p>
      </div>

      {/* Photo */}
      <div className="relative z-10 flex justify-center mt-4">
        {photo ? (
          <img
            src={photo}
            alt={cardName(member)}
            className="w-24 h-24 rounded-lg object-cover border-4 border-white shadow-lg bg-white"
          />
        ) : (
          <div className="w-24 h-24 rounded-lg border-4 border-white shadow-lg bg-[#0B5D1E] flex items-center justify-center text-3xl font-bold text-white">
            {initial(member)}
          </div>
        )}
      </div>

      {/* Identity */}
      <div className="text-center px-5 pt-3">
        <h2 className="text-lg font-bold text-gray-900 leading-tight">{cardName(member)}</h2>
        <p className="text-[#0B5D1E] font-semibold text-sm mt-0.5">{cardDesignation(member)}</p>
        <p className="text-gray-600 text-xs mt-1">Employment Category: {employmentCategory}</p>
        <span className="inline-block mt-2 bg-[#C8A951]/15 text-[#8a6d1f] text-xs font-bold tracking-wide px-3 py-1 rounded-full border border-[#C8A951]/40">
          ID: {member.membership_number || 'Pending'}
        </span>
      </div>

      {/* QR */}
      {qr && (
        <div className="flex flex-col items-center mt-4">
          <img src={qr} alt="Membership QR code" className="w-28 h-28" />
          <p className="text-[10px] text-gray-400 mt-1">Scan to verify membership</p>
        </div>
      )}

      {/* Contact details */}
      <div className="px-5 py-4 mt-2 border-t border-gray-100 space-y-2">
        <div className="flex items-start gap-2 text-[11px] text-gray-600">
          <svg className="w-3.5 h-3.5 text-[#0B5D1E] mt-[1px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
          <span>{ORG_INFO.address}</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-gray-600">
          <svg className="w-3.5 h-3.5 text-[#0B5D1E] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 010 18M12 3a15 15 0 000 18" /></svg>
          <span>{ORG_INFO.website}</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-gray-600">
          <svg className="w-3.5 h-3.5 text-[#0B5D1E] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
          <span>{ORG_INFO.email}</span>
        </div>
      </div>

      {/* Footer strip */}
      <div className="bg-[#062E10] h-2" />
    </div>
  );
}

/**
 * Build a fully self-contained (inline-styled) HTML document for the card
 * and open it in a print window. Independent of Tailwind so it renders
 * identically when printed or saved as PDF from any browser.
 */
export function printMembershipCard(member, logo) {
  const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
  ));
  const photo = resolveMedia(member.photo);
  const qr = resolveMedia(member.qr_code);
  const logoUrl = resolveMedia(logo) || `${window.location.origin}${DEFAULT_LOGO}`;
  const name = esc(cardName(member));
  const designation = esc(cardDesignation(member));
  const employmentCategory = esc(cardEmploymentCategory(member));
  const memberId = esc(member.membership_number || 'Pending');

  const photoBlock = photo
    ? `<img src="${esc(photo)}" style="width:96px;height:96px;border-radius:8px;object-fit:cover;border:4px solid #fff;box-shadow:0 4px 10px rgba(0,0,0,.15);background:#fff" />`
    : `<div style="width:96px;height:96px;border-radius:8px;border:4px solid #fff;background:#0B5D1E;color:#fff;display:flex;align-items:center;justify-content:center;font-size:34px;font-weight:700">${esc(initial(member))}</div>`;

  const qrBlock = qr
    ? `<div style="text-align:center;margin-top:14px"><img src="${esc(qr)}" style="width:120px;height:120px" /><div style="font-size:10px;color:#9ca3af;margin-top:4px">Scan to verify membership</div></div>`
    : '';

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Membership ID Card — ${name}</title>
<style>
  @page { margin: 12mm; }
  body { font-family: Arial, Helvetica, sans-serif; display:flex; justify-content:center; padding:20px; background:#f3f4f6; }
  .card { width:340px; background:#fff; border:1px solid #e5e7eb; border-radius:16px; overflow:hidden; box-shadow:0 10px 25px rgba(0,0,0,.1); }
  .hdr { background:linear-gradient(135deg,#062E10,#0B5D1E); padding:20px 20px 30px; text-align:center; }
  .hdr .row { display:flex; align-items:center; justify-content:center; gap:10px; }
  .hdr img { height:44px; width:44px; object-fit:contain; background:#fff; border-radius:50%; padding:4px; }
  .org { color:#fff; font-weight:700; font-size:13px; line-height:1.2; text-align:left; }
  .branch { color:#C8A951; font-size:11px; font-weight:600; letter-spacing:.4px; text-align:left; }
  .tag { margin-top:12px; display:inline-block; font-size:10px; text-transform:uppercase; letter-spacing:2px; color:rgba(255,255,255,.8); border:1px solid rgba(255,255,255,.25); border-radius:999px; padding:2px 12px; }
  .photo { display:flex; justify-content:center; margin-top:16px; }
  .name { text-align:center; font-size:18px; font-weight:700; color:#111827; margin-top:12px; }
  .desig { text-align:center; color:#0B5D1E; font-weight:600; font-size:14px; margin-top:2px; }
  .employment { text-align:center; color:#4b5563; font-size:12px; margin-top:4px; }
  .idbadge { display:inline-block; margin:8px auto 0; background:rgba(200,169,81,.15); color:#8a6d1f; font-size:12px; font-weight:700; letter-spacing:.4px; padding:4px 12px; border-radius:999px; border:1px solid rgba(200,169,81,.4); }
  .contact { padding:16px 20px; margin-top:10px; border-top:1px solid #f3f4f6; font-size:11px; color:#4b5563; line-height:1.5; }
  .contact div { margin-bottom:5px; }
  .contact b { color:#0B5D1E; }
  .foot { background:#062E10; height:8px; }
</style></head>
<body onload="window.print()">
  <div class="card">
    <div class="hdr">
      <div class="row">
        <img src="${esc(logoUrl)}" />
        <div><div class="org">${esc(ORG_INFO.name)}</div><div class="branch">${esc(ORG_INFO.branch)}</div></div>
      </div>
      <div class="tag">Membership ID Card</div>
    </div>
    <div class="photo">${photoBlock}</div>
    <div class="name">${name}</div>
    <div class="desig">${designation}</div>
    <div class="employment">Employment Category: ${employmentCategory}</div>
    <div style="text-align:center"><span class="idbadge">ID: ${memberId}</span></div>
    ${qrBlock}
    <div class="contact">
      <div><b>Address:</b> ${esc(ORG_INFO.address)}</div>
      <div><b>Website:</b> ${esc(ORG_INFO.website)}</div>
      <div><b>Email:</b> ${esc(ORG_INFO.email)}</div>
    </div>
    <div class="foot"></div>
  </div>
</body></html>`;

  const w = window.open('', '_blank', 'width=480,height=760');
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
